import React from 'react';
import { FileText, Search, Check, X, Download } from 'lucide-react';

const ReviewInboxSection = ({
  filteredUploads,
  uploadFilter,
  setUploadFilter,
  searchTerm,
  setSearchTerm,
  selectedUploads,
  setSelectedUploads,
  handleBulkApprove,
  handleDownloadFile,
  handleApproveUpload,
  handleRejectUpload
}) => {
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Review Inbox</h1>
          <p className="text-gray-500">All uploads needing your review</p>
        </div>
        <div className="flex gap-3">
          <div className="relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input 
              type="text" 
              placeholder="Search uploads..." 
              className="pl-10 pr-4 py-2 border rounded-lg w-64"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select 
            className="border rounded-lg px-3 py-2"
            value={uploadFilter}
            onChange={(e) => setUploadFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="pending">Pending Review</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
          {selectedUploads.length > 0 && (
            <button 
              onClick={handleBulkApprove}
              className="bg-green-500 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2"
              data-testid="bulk-approve"
            >
              <Check className="w-4 h-4" />
              Approve Selected ({selectedUploads.length})
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border">
        <div className="p-4 border-b bg-gray-50 flex items-center gap-4">
          <input 
            type="checkbox" 
            className="w-4 h-4 rounded"
            checked={selectedUploads.length === filteredUploads.filter(u => u.status === 'Pending Review' || u.status === 'pending').length && selectedUploads.length > 0}
            onChange={(e) => {
              if (e.target.checked) {
                setSelectedUploads(filteredUploads.filter(u => u.status === 'Pending Review' || u.status === 'pending').map(u => u.id));
              } else {
                setSelectedUploads([]);
              }
            }}
          />
          <span className="text-sm text-gray-600">
            Select all pending ({filteredUploads.filter(u => u.status === 'Pending Review' || u.status === 'pending').length} items)
          </span>
        </div>
        
        {filteredUploads.map((upload) => (
          <div key={upload.id} className="p-4 flex items-center gap-4 border-b hover:bg-gray-50">
            {(upload.status === 'Pending Review' || upload.status === 'pending') && (
              <input 
                type="checkbox" 
                className="w-4 h-4 rounded"
                checked={selectedUploads.includes(upload.id)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedUploads([...selectedUploads, upload.id]);
                  } else {
                    setSelectedUploads(selectedUploads.filter(id => id !== upload.id));
                  }
                }}
              />
            )}
            {upload.status !== 'Pending Review' && upload.status !== 'pending' && <div className="w-4" />}
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-purple-600" />
            </div>
            <div className="flex-1">
              <p className="font-medium">{upload.title || upload.file_name}</p>
              <p className="text-sm text-gray-500">
                {upload.user_name || upload.user_email} • {new Date(upload.uploaded_at).toLocaleString()}
                {upload.file_exists === false && (
                  <span className="ml-2 text-red-500 font-medium">(File unavailable)</span>
                )}
              </p>
            </div>
            <span className={`px-2 py-1 text-xs rounded-full ${
              (upload.status === 'Pending Review' || upload.status === 'pending') ? 'bg-yellow-100 text-yellow-700' :
              (upload.status === 'Approved' || upload.status === 'approved') ? 'bg-green-100 text-green-700' :
              'bg-red-100 text-red-700'
            }`}>
              {upload.status === 'pending' ? 'Pending Review' : upload.status}
            </span>
            <div className="flex gap-2">
              <button 
                onClick={() => handleDownloadFile(upload.file_url, upload.file_name, upload.file_exists)}
                className={`p-2 rounded-lg ${upload.file_exists === false ? 'cursor-not-allowed opacity-50' : 'hover:bg-gray-100'}`}
                title={upload.file_exists === false ? "File unavailable" : "Download"}
                disabled={upload.file_exists === false}
              >
                <Download className={`w-5 h-5 ${upload.file_exists === false ? 'text-gray-300' : 'text-gray-400'}`} />
              </button>
              {(upload.status === 'Pending Review' || upload.status === 'pending') && (
                <>
                  <button 
                    onClick={() => handleApproveUpload(upload.id)}
                    className="p-2 hover:bg-green-100 rounded-lg"
                    title="Approve"
                  >
                    <Check className="w-5 h-5 text-green-500" />
                  </button>
                  <button 
                    onClick={() => handleRejectUpload(upload.id)}
                    className="p-2 hover:bg-red-100 rounded-lg"
                    title="Reject"
                  >
                    <X className="w-5 h-5 text-red-500" />
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
        
        {filteredUploads.length === 0 && (
          <div className="p-12 text-center text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No uploads found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewInboxSection;
